import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { ProtectedRoute } from '@/components/ccms-components/auth/components/ProtectedRoute';
import { Loader, Center } from '@/components/ui';
import { BACKEND_ROLES } from '@/config/permissions';

/**
 * Store Management Routes
 *
 * Admin surface for the non-vendor merch catalogue (ProductManagement) and the
 * legacy shop orders and payments that consume it. Vendor-marketplace orders and
 * products stay in the Vendors domain.
 *
 * Every route requires Admin or ShopAdmin — the Store.* rights and the ShopAdmin
 * role are seeded by migration 236.
 */

// ===== Lazy: every Store page is admin-only, so none needs to be eager =====
const StoreDashboardPage = lazy(() => import('./dashboard'));
const StoreOrdersPage = lazy(() => import('./orders'));
const StoreOrderDetailPage = lazy(() => import('./orders/[id]'));
const StoreEditOrderPage = lazy(() => import('./orders/[id]/edit'));
const StoreNewOrderPage = lazy(() => import('./orders/new'));
const StoreNewOrdersQueuePage = lazy(() => import('./orders/queue'));
const StoreOrderBoardPage = lazy(() => import('./orders/board'));
const StorePaymentsPage = lazy(() => import('./payments'));
const StorePaymentDetailPage = lazy(() => import('./payments/[id]'));
const StoreCustomersPage = lazy(() => import('./customers'));
const StoreCustomerDetailPage = lazy(() => import('./customers/[email]'));
const StoreProductsPage = lazy(() => import('./products'));
const StoreProductDetailPage = lazy(() => import('./products/[id]'));
const StoreNewProductPage = lazy(() => import('./products/new'));
const StoreLowStockPage = lazy(() => import('./products/low-stock'));
const StoreStockMovementsPage = lazy(() => import('./products/stock-movements'));
const StorePickingListPage = lazy(() => import('./orders/picking-list'));
const StoreReconciliationPage = lazy(() => import('./reconciliation'));
const StoreReportsPage = lazy(() => import('./reports'));
const StoreReportDetailPage = lazy(() => import('./reports/[reportId]'));
const StoreLookupsPage = lazy(() => import('./lookups'));
const StoreLookupDetailPage = lazy(() => import('./lookups/[category]/[lookup]'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

const STORE_ADMIN_ROLES = [BACKEND_ROLES.ADMIN, BACKEND_ROLES.SHOP_ADMIN];

/** Wraps a page in the Store role gate. */
const guarded = (element: React.ReactNode) => (
  <ProtectedRoute requiredRoles={STORE_ADMIN_ROLES}>{element}</ProtectedRoute>
);

export default function StoreManagementRoutes() {
  return (
    <MainLayout domainKey="store">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={guarded(<StoreDashboardPage />)} />
          <Route path="orders" element={guarded(<StoreOrdersPage />)} />
          {/* literal segment before :id so it is not swallowed by the param */}
          <Route path="orders/new" element={guarded(<StoreNewOrderPage />)} />
          <Route path="orders/queue" element={guarded(<StoreNewOrdersQueuePage />)} />
          <Route path="orders/board" element={guarded(<StoreOrderBoardPage />)} />
          <Route path="orders/picking-list" element={guarded(<StorePickingListPage />)} />
          <Route path="orders/:id" element={guarded(<StoreOrderDetailPage />)} />
          <Route path="orders/:id/edit" element={guarded(<StoreEditOrderPage />)} />
          <Route path="payments" element={guarded(<StorePaymentsPage />)} />
          <Route path="payments/:id" element={guarded(<StorePaymentDetailPage />)} />
          <Route path="reconciliation" element={guarded(<StoreReconciliationPage />)} />
          <Route path="customers" element={guarded(<StoreCustomersPage />)} />
          <Route path="customers/:email" element={guarded(<StoreCustomerDetailPage />)} />
          <Route path="products" element={guarded(<StoreProductsPage />)} />
          {/* low-stock before :id so the literal segment is not eaten by the param */}
          <Route path="products/new" element={guarded(<StoreNewProductPage />)} />
          <Route path="products/low-stock" element={guarded(<StoreLowStockPage />)} />
          <Route path="products/stock-movements" element={guarded(<StoreStockMovementsPage />)} />
          <Route path="products/:id" element={guarded(<StoreProductDetailPage />)} />
          <Route path="reports" element={guarded(<StoreReportsPage />)} />
          <Route path="reports/:reportId" element={guarded(<StoreReportDetailPage />)} />
          <Route path="lookups" element={guarded(<StoreLookupsPage />)} />
          <Route path="lookups/:category/:lookup" element={guarded(<StoreLookupDetailPage />)} />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
